// The footer's link to Slowed Studio, kept out of the components so the
// decision can be unit-tested without a browser.
//
// The panel also runs on web pages (slowed.app's demo, Studio's own
// controls). On Studio the link would only reopen the page it sits on, so it
// is left out there.
import { buildToolUrl } from "./update-banner";

function hostnameOf(address) {
  try {
    return new URL(address).hostname.toLowerCase();
  } catch (_) {
    return null;
  }
}

// Whether `pageUrl` is Studio itself, going by where `toolUrl` points.
export function isStudioPage(pageUrl, toolUrl) {
  const studio = hostnameOf(toolUrl);
  return studio !== null && hostnameOf(pageUrl) === studio;
}

// Where the footer's Studio link goes, or null when it should not show:
// no address for Studio yet, or the listener is already there.
export function resolveStudioLink({ toolUrl, pageUrl, medium, version } = {}) {
  if (isStudioPage(pageUrl, toolUrl)) return null;
  return buildToolUrl(toolUrl, { medium, version });
}

// The part of the popup's state that is the sound, in the extension's own
// words: the keys the Studio bridge speaks (content/youtube/studio-bridge.js)
// and Studio's hand-off reads (slowed-studio src/ui/handoff.js). Nothing
// about the listener or the page travels with it.
export const SOUND_KEYS = [
  "isSlowedEnabled",
  "slowControl",
  "reverbAmount",
  "highPassMode",
  "highPassCutoff",
  "lowPassMode",
  "lowPassCutoff",
  "bitcrusherMode",
  "bitcrusherAmount",
  "bassBoostMode",
  "bassBoostAmount",
  "phaserMode",
  "phaserAmount",
  "echoMode",
  "echoAmount",
  "lofiTapeMode",
  "lofiHissVolume",
  "vinylCrackleMode",
  "vinylCrackleVolume",
  "rainyMode",
  "rainVolume",
  "coffeeShopMode",
  "coffeeShopVolume",
  "seashoreMode",
  "seashoreVolume",
  "berghainMode",
  "berghainVolume",
];

export function pickSound(state) {
  const sound = {};
  if (!state || typeof state !== "object") return sound;
  for (const key of SOUND_KEYS) {
    const value = state[key];
    if (typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value))) {
      sound[key] = value;
    }
  }
  return sound;
}

// The song on `pageUrl` as a link Studio opens: one YouTube video (watch
// pages, youtu.be, YouTube Music, shorts) or one SoundCloud track. Null for
// any other page: a home page, a playlist, a channel, another site.
export function songLinkOf(pageUrl) {
  let url;
  try {
    url = new URL(pageUrl);
  } catch (_) {
    return null;
  }
  if (url.protocol !== "https:") return null;
  const host = url.hostname.toLowerCase();
  if (["youtube.com", "www.youtube.com", "music.youtube.com", "m.youtube.com", "youtu.be"].includes(host)) {
    const id =
      host === "youtu.be"
        ? url.pathname.slice(1)
        : url.searchParams.get("v") || (/^\/(?:shorts|embed|live)\/([^/]+)/.exec(url.pathname) || [])[1];
    return /^[\w-]{11}$/.test(id || "") ? `https://www.youtube.com/watch?v=${id}` : null;
  }
  if (["soundcloud.com", "www.soundcloud.com", "m.soundcloud.com"].includes(host)) {
    const parts = url.pathname.split("/").filter(Boolean);
    const artistPages = ["tracks", "albums", "popular-tracks", "reposts", "likes", "followers", "following", "comments", "spotlight", "sets"];
    const sitePages = ["discover", "search", "stream", "you", "feed", "charts", "upload", "settings", "messages", "notifications", "pages", "terms-of-use", "imprint", "mobile", "pro", "jobs", "people", "tags"];
    const track =
      parts.length >= 2 &&
      parts.length <= 3 &&
      parts.every((part) => /^[\w-]+$/.test(part)) &&
      !sitePages.includes(parts[0]) &&
      !artistPages.includes(parts[1]);
    return track ? `https://soundcloud.com/${parts.join("/")}` : null;
  }
  return null;
}

// Where Save takes the song: Studio, with the song's link, its name and
// this sound in the address, so Studio puts it in the playlist and opens
// it. Null when there is no song to save on this page, no address for
// Studio, or the panel is on Studio already.
export function resolveStudioSave({ toolUrl, pageUrl, name, sound, medium, version } = {}) {
  if (isStudioPage(pageUrl, toolUrl)) return null;
  const link = songLinkOf(pageUrl);
  const base = buildToolUrl(toolUrl, { medium, version });
  if (!link || !base) return null;
  const url = new URL(base);
  url.searchParams.set("add", link);
  const title = String(name || "").replace(/\s+/g, " ").trim().slice(0, 200);
  if (title) url.searchParams.set("name", title);
  const picked = pickSound(sound);
  if (Object.keys(picked).length) url.searchParams.set("sound", JSON.stringify(picked));
  return url.toString();
}

// The page a panel is shown on when it runs inside a frame on a website.
// A srcdoc frame has no address of its own, so ask the page around it.
export function hostPageUrl(scope = typeof window !== "undefined" ? window : undefined) {
  if (!scope) return null;
  try {
    const href = scope.top?.location?.href;
    if (href && href !== "about:srcdoc") return href;
  } catch (_) {
    // A page on another origin: its address is not ours to read.
  }
  return (
    scope.location?.ancestorOrigins?.[0] ||
    scope.document?.referrer ||
    scope.location?.href ||
    null
  );
}
